import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../utils/formatters';
import './PredictionDetailModal.css';

export function PredictionDetailModal({ match: detail, onClose }) {
  const { t } = useTranslation();

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const resultColors = { H: '#3b82f6', D: '#f59e0b', A: '#ef4444' };

  if (!detail) return null;

  const getMarginLabel = (margin) => {
    if (margin < 0.10) return t('modal.margin_tight');
    if (margin < 0.20) return t('modal.margin_moderate');
    return t('modal.margin_clear');
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{t('modal.title')}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          <div className="modal-match">
            <div className="modal-team">{detail.home_team}</div>
            <div className="modal-score">
              {detail.home_score != null ? `${detail.home_score} - ${detail.away_score}` : t('common.vs')}
            </div>
            <div className="modal-team">{detail.away_team}</div>
          </div>
          <div className="modal-date">{formatDate(detail.date)}</div>

          {detail.home_xg != null && (
            <div className="modal-xg">
              <span>xG: {detail.home_xg.toFixed(2)}</span>
              <span className="xg-sep">—</span>
              <span>xG: {detail.away_xg.toFixed(2)}</span>
            </div>
          )}

          <div className={`modal-verdict ${detail.is_correct ? 'correct' : 'incorrect'}`}>
            {detail.is_correct ? t('modal.correct') : t('modal.incorrect')}
          </div>

          <div className="modal-results-row">
            <div className="modal-result-box">
              <span className="modal-result-label">{t('modal.prediction_label')}</span>
              <span className="modal-result-badge" style={{ background: resultColors[detail.predicted_result] }}>
                {t(`results.${detail.predicted_result}_short`)}
              </span>
            </div>
            <div className="modal-result-arrow">→</div>
            <div className="modal-result-box">
              <span className="modal-result-label">{t('modal.actual_label')}</span>
              <span className="modal-result-badge" style={{ background: resultColors[detail.actual_result] }}>
                {t(`results.${detail.actual_result}_short`)}
              </span>
            </div>
          </div>

          <div className="modal-probs-section">
            <h4>{t('modal.probabilities')}</h4>
            {[
              { key: 'home', code: 'H' },
              { key: 'draw', code: 'D' },
              { key: 'away', code: 'A' },
            ].map(({ key, code }) => {
              const prob        = detail.probabilities[key];
              const isPredicted = detail.predicted_result === code;
              const isActual    = detail.actual_result    === code;
              return (
                <div key={key} className={`modal-prob-row ${isPredicted ? 'predicted' : ''}`}>
                  <div className="modal-prob-label">
                    {t(`results.${code}_short`)}
                    {isPredicted && <span className="tag predicted-tag">{t('modal.chosen')}</span>}
                    {isActual    && <span className="tag actual-tag">{t('modal.real')}</span>}
                  </div>
                  <div className="modal-prob-bar-wrap">
                    <div className="modal-prob-bar-fill" style={{
                      width: `${(prob * 100).toFixed(1)}%`,
                      background: resultColors[code],
                      opacity: isPredicted ? 1 : 0.5,
                    }} />
                  </div>
                  <div className="modal-prob-value">{(prob * 100).toFixed(1)}%</div>
                </div>
              );
            })}
          </div>

          {detail.decision_margin != null && (
            <div className="modal-margin">
              <span className="margin-label">{t('modal.margin_label')}</span>
              <span className={`margin-value ${detail.decision_margin < 0.10 ? 'tight' : 'clear'}`}>
                {(detail.decision_margin * 100).toFixed(1)}% {getMarginLabel(detail.decision_margin)}
              </span>
            </div>
          )}

          <div className="modal-footer-info">{t('modal.model_label')} {detail.model_version}</div>
        </div>
      </div>
    </div>
  );
}